// Angular
import { Component, OnInit, OnDestroy } from '@angular/core';
// Layout
import { LayoutConfigService } from '../../../core/_base/layout';
// Object-Path
import * as objectPath from 'object-path';
// Environment
import { environment } from 'environments/environment';

import { TranslationService } from '../../../core/_base/layout';

import { PolicyService } from '@app/api/policy.service';
import { SystemSettingStore } from '@app/services/change-setting.service';
import { SystemSettingService } from '@app/api/systemsettting.service';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { forkJoin, of, Subject } from 'rxjs';

@Component({
  selector: 'kt-footer2',
  templateUrl: './footer2.component.html',
  styleUrls: ['./footer2.component.scss'],
})
export class FooterComponent2 implements OnInit, OnDestroy {
  // Public properties
  today: number = Date.now();
  fluid: boolean;
  lstPolicy: any[];
  urlLogo: string;

  // Map localStorage language keys to footer types
  private langToFooterType = {
    vi: 'FOOTER-VN',
    en: 'FOOTER-EN',
    zh: 'FOOTER-ZH',
  };

  // Store all footer data
  private footerDataMap = {
    vi: [],
    en: [],
    zh: [],
  };

  // Current displayed footer list
  listCurrentLang: any[] = [];

  private destroy$ = new Subject<void>();

  isVietNamese: boolean = true;
  fullYear = new Date().getFullYear();
  hotline: string;
  email: string;

  constructor(
    private storeSystem: SystemSettingStore,
    private system: SystemSettingService,
    private layoutConfigService: LayoutConfigService,
    private policy: PolicyService,
    private translationService: TranslationService
  ) {}

  ngOnInit(): void {
    const config = this.layoutConfigService.getConfig();
    this.fluid = objectPath.get(config, 'footer.self.width') === 'fluid';

    this.getPolicies();
    this.getLogo();
    this.subscribeToLogoUpdates();
    this.getHotLine();
    this.getEmail();

    // Load all footer data first
    this.loadAllFooterData();

    // Subscribe to language changes
    this.translationService.language$.pipe(takeUntil(this.destroy$)).subscribe((lang) => {
      this.updateFooterByLanguage(lang);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load all footer data for all languages
   */
  loadAllFooterData(): void {
    forkJoin([this.getListFooter('vi'), this.getListFooter('en'), this.getListFooter('zh')])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([listVi, listEn, listZh]) => {
        this.footerDataMap.vi = listVi;
        this.footerDataMap.en = listEn;
        this.footerDataMap.zh = listZh;

        // Set initial footer based on current language
        const currentLang = this.translationService.getSelectedLanguage();
        this.updateFooterByLanguage(currentLang);
      });
  }

  /**
   * Update footer display based on language
   */
  updateFooterByLanguage(lang: string): void {
    // Map language key to match footerDataMap keys
    const langKey = this.mapLanguageKey(lang);

    if (this.footerDataMap[langKey]) {
      this.listCurrentLang = this.footerDataMap[langKey];
    }
  }

  /**
   * Map language keys (handle variations)
   */
  private mapLanguageKey(lang: string): string {
    // Handle possible variations
    const langLower = lang.toLowerCase();

    if (langLower === 'vi' || langLower === 'vn') return 'vi';
    if (langLower === 'zh' || langLower === 'ch' || langLower === 'cn') return 'zh';
    if (langLower === 'en') return 'en';

    // Default to English if unknown
    return 'en';
  }

  /**
   * Get footer list by language
   */
  getListFooter(lang: string) {
    const footerType = this.langToFooterType[lang];

    if (!footerType) {
      console.error('Unknown language: ' + lang);
      return of([]);
    }

    return this.system.apiSystemSettingGet('type==' + footerType).pipe(
      switchMap((res) => {
        let parents = [];

        if (
          res &&
          res.data &&
          res.data.collection &&
          res.data.collection.length > 0 &&
          res.data.collection[0].children
        ) {
          parents = res.data.collection[0].children.filter(function (item) {
            return !item.is_hidden;
          });
        }

        if (parents.length === 0) {
          return of([]);
        }

        const parentRequests = parents.map((parent) =>
          this.system.apiSystemSettingGet('parent_id==' + parent.id).pipe(
            switchMap((childRes) => {
              let children = [];

              if (
                childRes &&
                childRes.data &&
                childRes.data.collection &&
                childRes.data.collection.length > 0 &&
                childRes.data.collection[0].children
              ) {
                children = childRes.data.collection[0].children.filter(function (child) {
                  return !child.is_hidden;
                });
              }

              if (children.length === 0) {
                return of({
                  id: parent.id,
                  language: parent.language,
                  name: parent.name,
                  slug: parent.slug,
                  sort_order: parent.sort_order,
                  value: parent.value,
                  children: [],
                });
              }


              const childDetailRequests = children.map((child) =>
                this.system.apiSystemSettingIdGet(child.id).pipe(
                  map((detailRes) => {
                    let childType = null;

                    if (detailRes && detailRes.data) {
                      childType = detailRes.data.type;
                    }

                    return {
                      id: child.id,
                      name: child.name,
                      value: child.value,
                      sort_order: child.sort_order,
                      is_hidden: child.is_hidden,
                      slug: detailRes.data.slug,
                      type: childType,
                    };
                  })
                )
              );

              return forkJoin(childDetailRequests).pipe(
                map((childrenWithType) => {
                  return {
                    id: parent.id,
                    language: parent.language,
                    name: parent.name,
                    slug: parent.slug,
                    sort_order: parent.sort_order,
                    value: parent.value,
                    children: childrenWithType,
                  };
                })
              );
            })
          )
        );

        return forkJoin(parentRequests).pipe(map((result) => this.sortByOrder(result)));
      })
    );
  }

  sortByOrder(list: any[]) {
    return list ? list.slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)) : [];
  }

  getHotLine() {
    this.system.apiSystemSettingGet('type==HOTLINE-CALL').subscribe((res) => {
      if (res.success) {
        this.hotline = res.data.collection[0].children[0].value;
      }
    });
  }

  getEmail() {
    this.system.apiSystemSettingGet('type==EMAIL').subscribe((res) => {
      if (res.success) {
        this.email = res.data.collection[0].children[0].value;
      }
    });
  }

  getLogo() {
    this.system
      .apiSystemSettingGet('type==HEADER-ICONS')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.success && res.data.collection && res.data.collection.length > 0) {
            const children = res.data.collection[0].children;
            if (children && children.length > 0) {
              const logo = children.find(
                (item) => item.value && item.value !== '' && item.is_hidden === false
              );

              if (logo && logo.value) {
                this.urlLogo = environment.backendhost + logo.value;
              }
            }
          }
        },
        error: (err) => {
          console.error('Error loading logo:', err);
        },
      });
  }

  subscribeToLogoUpdates() {
    this.storeSystem.settings$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (settings) => {
        if (settings && settings.logos && settings.logos.length > 0) {
          const activeLogo = settings.logos.find(
            (item) => item.value && item.value !== '' && item.is_hidden === false
          );

          if (activeLogo && activeLogo.value) {
            this.urlLogo = activeLogo.value.startsWith('http')
              ? activeLogo.value
              : environment.backendhost + activeLogo.value;
          }
        }
      },
      error: (err) => {
        console.error('Error subscribing to settings:', err);
      },
    });
  }

  getPolicies() {
    this.policy.apiPoliciesGet().subscribe((res) => {
      if (res.success) {
        this.lstPolicy = res.data;
      }
    });
  }
}
